import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { DictationActivityEvent } from 'src/app/shared/models';
import { PipelineActivityLogService } from 'src/app/shared/services/pipeline-activity-log.service';
import { PatientActivityModalComponent } from 'src/app/shared/components/patient-activity-modal/patient-activity-modal.component';
import { formatPatientName, getAvatarColor, getInitials } from 'src/app/shared/utils/avatar.util';

const CATEGORY_ICONS: { [key: string]: string } = {
  insurance_harvest: 'fa-id-card',
  ort_master_harvest: 'fa-notes-medical',
  dictation: 'fa-microphone-alt',
  action_item: 'fa-tasks',
  order: 'fa-file-medical',
  medical_necessity: 'fa-shield-alt',
  appointment_processing: 'fa-calendar-times',
};

const CATEGORY_LABELS: { [key: string]: string } = {
  insurance_harvest: 'Insurance',
  ort_master_harvest: 'Medical Records',
  dictation: 'Dictation',
  action_item: 'Action Item',
  order: 'Order',
  medical_necessity: 'Medical Necessity',
  appointment_processing: 'Appointment',
};

@Component({
  selector: 'app-automation-activity',
  templateUrl: './automation-activity.component.html',
  styleUrls: ['./automation-activity.component.scss'],
})
export class AutomationActivityComponent implements OnInit, OnDestroy {
  rows: DictationActivityEvent[] = [];
  loading = false;
  initialLoad = true;
  refreshInterval: any;
  private searchDebounce: any;

  levelFilter = '';
  practiceFilter = '';
  patientNameFilter = '';

  currentPage = 1;
  nextPage: number | null = null;
  previousPage: number | null = null;
  total = 0;
  readonly pageSize = 25;

  constructor(
    private pipelineActivityLogService: PipelineActivityLogService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.fetchLogs();
    this.refreshInterval = setInterval(() => this.fetchLogs(true), 10 * 1000);
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
    clearTimeout(this.searchDebounce);
  }

  fetchLogs(isBackgroundRefresh: boolean = false): void {
    if (!isBackgroundRefresh) this.loading = true;
    this.pipelineActivityLogService
      .getActivityLogs({
        page: this.currentPage,
        limit: this.pageSize,
        level: this.levelFilter,
        practice_id: this.practiceFilter,
        patient_name: this.patientNameFilter,
      })
      .then((res) => {
        this.loading = false;
        this.initialLoad = false;
        const payload = res?.data;
        this.rows = (payload?.data ?? []).filter(
          (row) => !(row.category === 'action_item' && (row.step === 'approved' || row.step === 'rejected'))
        );
        this.currentPage = payload?.currentPage ?? 1;
        this.nextPage = payload?.nextPage ?? null;
        this.previousPage = payload?.previousPage ?? null;
        this.total = payload?.total ?? 0;
      })
      .catch((err) => {
        this.loading = false;
        this.initialLoad = false;
        console.error('Failed to load activity logs:', err);
      });
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.fetchLogs();
  }

  onSearchInput(): void {
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.onFilterChange(), 400);
  }

  clearFilters(): void {
    this.levelFilter = '';
    this.practiceFilter = '';
    this.patientNameFilter = '';
    this.onFilterChange();
  }

  get hasActiveFilters(): boolean {
    return !!(this.levelFilter || this.practiceFilter || this.patientNameFilter);
  }

  goToPage(page: number | null): void {
    if (!page) return;
    this.currentPage = page;
    this.fetchLogs();
  }

  categoryIcon(category: string): string {
    return CATEGORY_ICONS[category] || 'fa-circle-info';
  }

  categoryLabel(category: string): string {
    return CATEGORY_LABELS[category] || this.humanize(category);
  }

  patientLabel(row: DictationActivityEvent): string {
    if (!row.patient) return '—';
    return formatPatientName(row.patient.first_name, row.patient.last_name) || '—';
  }

  patientInitials(row: DictationActivityEvent): string {
    return getInitials(this.patientLabel(row));
  }

  patientAvatarColor(row: DictationActivityEvent): string {
    return getAvatarColor(row.patient?.uniq_id || row.patient_id || this.patientLabel(row));
  }

  openLiveOperations(): void {
    window.open('/live-operations', '_blank');
  }

  openPatientActivity(row: DictationActivityEvent): void {
    if (!row.patient) return;
    const modalRef = this.modalService.open(PatientActivityModalComponent, { size: 'lg' });
    modalRef.componentInstance.patientId = row.patient.uniq_id;
    modalRef.componentInstance.patientName = this.patientLabel(row);
    modalRef.componentInstance.mrn = row.patient.med_rec_nbr;
  }

  formatTime(value: string): string {
    return moment(value).format('MMM D, h:mm:ss.SSS A');
  }

  private humanize(value: string): string {
    if (!value) return '';
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
